import { action, extendObservable, isObservableObject, observable } from "mobx";
import { Observer } from "mobx-react";
import React from "react";
import { QueryClient } from "react-query";
import { DisposerTree } from "../../../WSUtils/disposerTree";
import { UserInfo } from "../../../WSUtils/UserInfo";
import { concatClassnames } from "../../../WSUtils/util";
import { CVEditContextStore } from "../CVEditContext";
import { getValueVersioner } from "../../../WSUtils/getValueVersioner";

export type GetOnRenderComponentProps = (
  props: OnRenderCVEditComponentProps
) => any;

export interface OnRenderCVEditComponentProps {
  def: CVEditBaseFieldDef;
  defaultRender?: OnRenderCvEditComponent;
}

export type OnRenderCvEditComponent<
  P extends OnRenderCVEditComponentProps = OnRenderCVEditComponentProps
> = (props: P) => any;

export interface OnRenderCVEditErrorMessageProps {
  def: CVEditBaseFieldDef;
  defaultRender?: OnRenderCvEditErrorMessage;
}

export type OnRenderCvEditErrorMessage = (
  props: OnRenderCVEditErrorMessageProps
) => any;

export interface OnRenderCVEditContainerProps {
  children?: any;

  defaultRender?: OnRenderCvEditContainer;
  containerProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
}

export type OnRenderCvEditContainer = (
  props: OnRenderCVEditContainerProps
) => any;

export function CVEditBaseField<
  P extends OnRenderCVEditComponentProps = OnRenderCVEditComponentProps,
  D extends CVEditBaseFieldDef<any, P> = CVEditBaseFieldDef<any, P>,
  X extends CVEditBaseFieldProps<P, D> = CVEditBaseFieldProps<P, D>
>(props: X) {
  const { def, containerProps = {}, ...rest } = props;

  return (
    <Observer
      render={() => {
        let OnRenderComponent =
          def.onRenderComponent ||
          def.defaultOnRenderComponent ||
          defaultOnRenderComponent;

        if (props.renderOnlyComponent) {
          return (
            <OnRenderComponent
              def={def}
              defaultRender={
                def.defaultOnRenderComponent || defaultOnRenderComponent
              }
              {...rest}
            />
          );
        }

        let OnRenderErrorMessage =
          def.onRenderErrorMessage ||
          def.defaultOnRenderErrorMessage ||
          defaultOnRenderErrorMessage;

        let OnRenderContainer =
          def.onRenderContainer ||
          def.defaultOnRenderContainer ||
          defaultOnRenderContainer;

        const label = def.getLabel();

        return (
          <OnRenderContainer
            containerProps={{
              ...containerProps,
              className: concatClassnames([
                "p-field",
                containerProps.className,
              ]),
            }}
            {...containerProps}
            defaultRender={
              def.defaultOnRenderContainer || defaultOnRenderContainer
            }
          >
            {!!label && (
              <label className="block" htmlFor={def.id}>
                {label}
              </label>
            )}

            <OnRenderComponent
              def={def}
              defaultRender={
                def.defaultOnRenderComponent || defaultOnRenderComponent
              }
              {...rest}
            />

            <OnRenderErrorMessage
              def={def}
              defaultRender={
                def.defaultOnRenderErrorMessage || defaultOnRenderErrorMessage
              }
            />
          </OnRenderContainer>
        );
      }}
    />
  );
}

const defaultOnRenderContainer: OnRenderCvEditContainer = (props) => (
  <div {...props.containerProps}>{!!props.children && props.children}</div>
);

const defaultOnRenderErrorMessage: OnRenderCvEditErrorMessage = ({ def }) => (
  <Observer
    render={() =>
      !!def.store.visibleValidationError && (
        <small id={def.id + "-help"} className="p-error">
          {def.store.visibleValidationError}
        </small>
      )
    }
  />
);

const defaultOnRenderComponent: OnRenderCvEditComponent = ({ def }) => (
  <Observer render={() => <>defaultOnRenderComponent</>} />
);

export interface CVEditBaseFieldProps<
  P extends OnRenderCVEditComponentProps = OnRenderCVEditComponentProps,
  D extends CVEditBaseFieldDef<any, P> = CVEditBaseFieldDef<any, P>
> {
  def: D;
  defaultOnRenderComponent?: OnRenderCvEditComponent;

  containerProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
  children?: any;
  renderOnlyComponent?: boolean;
}

export interface CVEditBaseFieldDef<
  STORE extends CVEditFieldStore = CVEditFieldStore,
  P extends OnRenderCVEditComponentProps = OnRenderCVEditComponentProps
> {
  store: STORE;
  id: string;
  getLabel: () => any;
  onRenderComponent?: OnRenderCvEditComponent<P>;
  defaultOnRenderComponent?: OnRenderCvEditComponent;
  onRenderErrorMessage?: OnRenderCvEditComponent;
  defaultOnRenderErrorMessage?: OnRenderCvEditComponent;
  onRenderContainer?: OnRenderCvEditContainer;
  defaultOnRenderContainer?: OnRenderCvEditContainer;
  defaultGetOnRenderComponentProps?: GetOnRenderComponentProps;
  hasUnsavedChanges: () => [string, any, any] | false;
  valueVersioner?: ValueVersioner;
}

export interface CreateBaseFieldDefArgs {
  getLabel: () => any;
  id: string;
  onRenderComponent?: OnRenderCvEditComponent;
}

export function cveField<
  P extends OnRenderCVEditComponentProps = OnRenderCVEditComponentProps,
  STORE extends CVEditFieldStore = CVEditFieldStore
>(
  initField: (
    _valueVersioner: typeof getCvEditFieldValueVersioner
  ) => CVEditBaseFieldDef<STORE, P>
): CVEditBaseFieldDef<STORE, P> {
  return initField(getCvEditFieldValueVersioner);
}

export type ValueVersioner = ReturnType<typeof getCvEditFieldValueVersioner>;

export const getCvEditFieldValueVersioner = (
  store: CVEditFieldStore,
  debounce: number,
  canDebounceMutations: boolean,
  fieldId: string
) => {
  return getValueVersioner({
    debounce,
    canDebounceMutations,
    setValue: action((value) => {
      store.value = value;
    }),
    setValueVersion: action((valueVersion) => {
      store.valueVersion = valueVersion;
    }),
    getValueVersion: () => store.valueVersion,
  });
};

export interface CvEditFieldParams<
  STORE extends CVEditFieldStore = CVEditFieldStore
> {
  id: string;
  fieldName: string;
  cvEditContext: CVEditContextStore;
  userInfo: UserInfo;
  getValueVersioner: typeof getCvEditFieldValueVersioner;
  queryClient: QueryClient;
  onRenderComponent?: OnRenderCvEditComponent;
  onRenderErrorMessage?: OnRenderCvEditErrorMessage;
  onRenderContainer?: OnRenderCvEditContainer;
  dt: DisposerTree;
  label: string;
  valueValidation?: (
    value: STORE["value"]
  ) => string | void | false | null | undefined;
}

export type CVEFieldDefFactory<
  STORE extends CVEditFieldStore = CVEditFieldStore,
  PARAMS extends CvEditFieldParams = CvEditFieldParams
> = (args: PARAMS) => CVEditBaseFieldDef<STORE>;

export interface CVEditIncompleteFieldStore<V = any> {
  readonly validationError: string;
  readonly showValidationError?: boolean;
  value: V;

  [key: string]: any;
}

export interface CVEditFieldStore<V = any>
  extends CVEditIncompleteFieldStore<V> {
  [key: string]: any;

  valueVersion: number;
  onChangeCount: number;
  readonly visibleValidationError: string;
}

export function cveFieldStore<S extends CVEditIncompleteFieldStore>(
  store: S
): S & CVEditFieldStore<S["value"]> {
  if (!isObservableObject(store)) store = observable(store);

  if (store.valueVersion) throw new Error("valueVersion is overwritten");
  if (store.onChangeCount) throw new Error("onChangeCount is overwritten");
  if (store.visibleValidationError)
    throw new Error("visibleValidationError is overwritten");

  let extendedStore = extendObservable(store, {
    valueVersion: 0,
    onChangeCount: 0,

    get visibleValidationError() {
      if (store.showValidationError === true)
        return extendedStore.validationError;

      return null;
    },
  });

  return extendedStore;
}
